import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Badge } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../../utils/axiosConfig';
import './VehicleImages.css';

const VehicleImages = () => {
    const { vehicleId } = useParams();
    const navigate = useNavigate();

    const [vehicle, setVehicle] = useState(null);
    const [allImages, setAllImages] = useState([]);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingImage, setEditingImage] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState('');

    const [formData, setFormData] = useState({
        category: 'gallery',
        image_url: '',
        alt_text_en: '',
        alt_text_ar: '',
        is_primary: false,
        sort_order: 0
    });

    const categories = ['exterior', 'interior', 'gallery'];

    const fetchVehicleData = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/vehicles/${vehicleId}`);
            const vehicleData = response.data.data || response.data;
            setVehicle(vehicleData);
            const vehicleImages = vehicleData.images || [];
            setAllImages(vehicleImages);
            setImages(vehicleImages);
        } catch (error) {
            console.error('Error fetching vehicle:', error);
            toast.error('Failed to load vehicle data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVehicleData();
    }, [vehicleId]);

    useEffect(() => {
        if (categoryFilter) {
            setImages(allImages.filter(img => img.category === categoryFilter));
        } else {
            setImages(allImages);
        }
    }, [categoryFilter, allImages]);

    const handleOpenModal = (image = null) => {
        if (image) {
            setEditingImage(image);
            setFormData({
                category: image.category || 'gallery',
                image_url: image.image_url || '',
                alt_text_en: image.alt_text_en || '',
                alt_text_ar: image.alt_text_ar || '',
                is_primary: image.is_primary || false,
                sort_order: image.sort_order || 0
            });
        } else {
            setEditingImage(null);
            setFormData({
                category: 'gallery',
                image_url: '',
                alt_text_en: '',
                alt_text_ar: '',
                is_primary: false,
                sort_order: 0
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingImage(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingImage) {
                await axiosInstance.put(`/admin/vehicles/${vehicle.id}/images/${editingImage.id}`, formData);
                toast.success('Image updated successfully!');
            } else {
                await axiosInstance.post(`/admin/vehicles/${vehicle.id}/images`, formData);
                toast.success('Image added successfully!');
            }
            handleCloseModal();
            fetchVehicleData();
        } catch (error) {
            console.error('Error saving image:', error);
            toast.error(error.response?.data?.message || 'Failed to save image');
        }
    };

    const handleDelete = async (imageId) => {
        if (!window.confirm('Are you sure you want to delete this image?')) return;

        try {
            await axiosInstance.delete(`/admin/vehicles/${vehicle.id}/images/${imageId}`);
            toast.success('Image deleted successfully!');
            fetchVehicleData();
        } catch (error) {
            console.error('Error deleting image:', error);
            toast.error('Failed to delete image');
        }
    };

    if (loading) {
        return <Container className="mt-5"><div className="text-center">Loading...</div></Container>;
    }

    return (
        <Container fluid className="mt-4">

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button variant="outline-secondary" onClick={() => navigate(`/vehicle-management/view/${vehicleId}`)} className="me-2">
                        Back to Vehicle
                    </Button>
                    <h3 className="d-inline">Manage Images - {vehicle?.title_en}</h3>
                </div>
                <Button variant="primary" onClick={() => handleOpenModal()}>
                    Add Image
                </Button>
            </div>

            <Card className="mb-4">
                <Card.Body>
                    <Form.Group>
                        <Form.Label>Filter by Category</Form.Label>
                        <Form.Select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="w-auto"
                        >
                            <option value="">All Categories</option>
                            {categories.map(cat => (
                                <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Card.Body>
            </Card>

            <Row>
                {images.length === 0 ? (
                    <Col>
                        <Card>
                            <Card.Body className="text-center">No images found</Card.Body>
                        </Card>
                    </Col>
                ) : (
                    images.map((image) => (
                        <Col md={3} key={image.id} className="mb-4">
                            <Card>
                                <Card.Img
                                    variant="top"
                                    src={image.image_url}
                                    style={{ height: '200px', objectFit: 'cover' }}
                                    onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
                                    }}
                                />
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Badge bg="secondary">{image.category}</Badge>
                                        {image.is_primary && <Badge bg="success">Primary</Badge>}
                                    </div>
                                    <small className="text-muted d-block mb-2">
                                        {image.alt_text_en || 'No alt text'}
                                    </small>
                                    <div className="d-flex gap-2">
                                        <Button variant="warning" size="sm" onClick={() => handleOpenModal(image)}>
                                            Edit
                                        </Button>
                                        <Button variant="danger" size="sm" onClick={() => handleDelete(image.id)}>
                                            Delete
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))
                )}
            </Row>

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingImage ? 'Edit Image' : 'Add Image'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Category</Form.Label>
                            <Form.Select
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                required
                            >
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Image URL</Form.Label>
                            <Form.Control
                                type="text"
                                name="image_url"
                                value={formData.image_url}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Alt Text (EN)</Form.Label>
                            <Form.Control
                                type="text"
                                name="alt_text_en"
                                value={formData.alt_text_en}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Alt Text (AR)</Form.Label>
                            <Form.Control
                                type="text"
                                name="alt_text_ar"
                                value={formData.alt_text_ar}
                                onChange={handleInputChange}
                                dir="rtl"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                name="is_primary"
                                label="Is Primary Image"
                                checked={formData.is_primary}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Sort Order</Form.Label>
                            <Form.Control
                                type="number"
                                name="sort_order"
                                value={formData.sort_order}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                        <Button variant="primary" type="submit">
                            {editingImage ? 'Update' : 'Add'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default VehicleImages;
